import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { CONSTANTS } from '../constants/constants';
import { ISharedData } from '../state-management/state-interfaces';
import { StateManagementService } from './state-management.service';
import { FilterUserParameter } from 'src/app/user-management/user-management-interfaces';

@Injectable({
  providedIn: 'root',
})
export class SharedDataService {
  public markAll: boolean = false;
  editMode = false;
  private sharedData: ISharedData = {
    headerTab: CONSTANTS.DASHBOARD,
    selectedSite: {
      siteName: '',
      siteId: '',
      siteTimeZone: '',
      commonAccessControl: true,
    },
    selectedBuilding: {
      buildingName: null,
      buildingId: null,
    },
    selectedFloor: {
      floorName: null,
      floorId: null,
    },
    selectedUnit: {
      unitName: null,
      unitId: null,
    },
    selectedAccessPoint: {
      accessPointName: null,
      accessPointId: null,
    },
    selectedMasterCredential: null,
    editMode: {
      building: false,
      floor: false,
    },
  };

  private sharedDataSubject = new BehaviorSubject<any>(this.sharedData);
  public unitData = new BehaviorSubject<boolean>(false);
  public userFilterData!: FilterUserParameter[] | undefined;
  public searchFilterUserData!: string;
  currentData = this.sharedDataSubject.asObservable();
  currentValue = this.sharedDataSubject.getValue();

  constructor(private stateManagementService: StateManagementService) {
    this.sharedDataSubject.next(this.stateManagementService.getSharedData());
  }

  setHeaderTab(value: any) {
    let valueForUpdate: ISharedData =
      this.stateManagementService.getSharedData();
    valueForUpdate.headerTab = value;
    this.sharedDataSubject.next(valueForUpdate);
    this.stateManagementService.updateSharedData(valueForUpdate);
  }

  checkFlag(value: boolean) {
    this.unitData.next(value);
  }
  /**
   * @description This function is responsible for maintaining and storing the user's tab filter records.
   * @returns void
   */
  set filterUser(value: FilterUserParameter[] | undefined) {
    this.userFilterData = value!.length > 0 ? value : undefined;
  }

  /**
   * @descriptionThis function is responsible for user tab returning the updated value.
   * @returns user filters
   */
  get filterUser() {
    return this.userFilterData;
  }

  /**
   * @description This function is responsible for maintaining and storing the user's tab search word records.
   * @returns void
   */
  set searchFilterUserWord(value: string) {
    this.searchFilterUserData = value;
  }

  /**
   * @descriptionThis function is responsible for user tab returning the updated search word value.
   * @returns user filters
   */
  get searchFilterUserWord() {
    return this.searchFilterUserData;
  }

  setSelectedSite(value: any) {
    let valueForUpdate: ISharedData =
      this.stateManagementService.getSharedData();
    if (
      valueForUpdate.selectedSite.siteId === value.siteId &&
      valueForUpdate.selectedSite.siteName === value.siteName &&
      valueForUpdate.selectedSite.siteTimeZone === value.siteTimeZone
    ) {
      return;
    }
    valueForUpdate.selectedSite = value;
    valueForUpdate.selectedBuilding = { buildingName: null, buildingId: null };
    valueForUpdate.selectedFloor = { floorName: null, floorId: null };
    valueForUpdate.selectedUnit = { unitName: null, unitId: null };
    valueForUpdate.selectedAccessPoint = {
      accessPointName: null,
      accessPointId: null,
    };
    valueForUpdate.editMode = {
      building: false,
      floor: false,
    };
    this.sharedDataSubject.next(valueForUpdate);
    this.stateManagementService.updateSharedData(valueForUpdate);
  }

  setSelectedBuilding(value: any) {
    let valueForUpdate: ISharedData =
      this.stateManagementService.getSharedData();
    valueForUpdate.selectedBuilding = value;
    valueForUpdate.selectedFloor = { floorName: null, floorId: null };
    valueForUpdate.selectedUnit = { unitName: null, unitId: null };
    valueForUpdate.selectedAccessPoint = {
      accessPointName: null,
      accessPointId: null,
    };
    this.sharedDataSubject.next(valueForUpdate);
    this.stateManagementService.updateSharedData(valueForUpdate);
  }

  updateSelectedBuilding(value: any) {
    let valueForUpdate: ISharedData =
      this.stateManagementService.getSharedData();
    valueForUpdate.selectedBuilding = value;
    this.sharedDataSubject.next(valueForUpdate);
    this.stateManagementService.updateSharedData(valueForUpdate);
  }

  setSelectedFloor(value: any) {
    let valueForUpdate: ISharedData =
      this.stateManagementService.getSharedData();
    valueForUpdate.selectedFloor = value;
    this.sharedDataSubject.next(valueForUpdate);
    this.stateManagementService.updateSharedData(valueForUpdate);
  }

  setSelectedUnit(value: any) {
    let valueForUpdate: ISharedData =
      this.stateManagementService.getSharedData();
    valueForUpdate.selectedUnit = value;
    this.sharedDataSubject.next(valueForUpdate);
    this.stateManagementService.updateSharedData(valueForUpdate);
  }

  setSelectedAccessPoint(value: any) {
    let valueForUpdate: ISharedData =
      this.stateManagementService.getSharedData();
    valueForUpdate.selectedAccessPoint = value;
    this.sharedDataSubject.next(valueForUpdate);
    this.stateManagementService.updateSharedData(valueForUpdate);
  }

  setSelectedMasterCredential(value: any) {
    let valueForUpdate: ISharedData =
      this.stateManagementService.getSharedData();
    valueForUpdate.selectedMasterCredential = value;
    this.sharedDataSubject.next(valueForUpdate);
    this.stateManagementService.updateSharedData(valueForUpdate);
  }

  setEditMode(value: any) {
    let valueForUpdate: ISharedData =
      this.stateManagementService.getSharedData();
    valueForUpdate.editMode = value;
    this.sharedDataSubject.next(valueForUpdate);
    this.stateManagementService.updateSharedData(valueForUpdate);
  }

  resetSharedData() {
    this.setSelectedSite({
      siteName: null,
      siteId: null,
      siteTimeZone: null,
      commonAccessControl: true,
    });
    this.setSelectedUnit({
      unitName: null,
      unitId: null,
    });
    this.setSelectedFloor({
      floorId: null,
      floorName: null,
    });
  }
  setAllEntityData(access: any) {
    // Set the selected building data
    this.setSelectedBuilding({
      buildingId: access?.buildingId,
      buildingName: access?.buildingName,
    });
    // Set the selected floor data
    this.setSelectedFloor({
      floorId: access?.floorId,
      floorName: access?.floorName,
    });
    // Check if the access object has an access point or unit ID and set the corresponding data
    if (access?.accessPointId) {
      // Set the selected access point data
      this.setSelectedAccessPoint({
        accessPointId: access?.accessPointId,
        accessPointName: access?.accessPointName,
      });
    } else if (access?.unitId) {
      // Set the selected unit data
      this.setSelectedUnit({
        unitId: access?.unitId,
        unitName: access?.unitPrefix
          ? access?.unitPrefix + access?.unitName
          : access?.unitName,
      });
    }
  }
}
